import { createContext, useContext, useEffect, useState, useSyncExternalStore } from "react";
import type { Session } from "./session";
import type { State } from "./sync/store";
import type { Principal } from "./sync/types";

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error("no session");
  return s;
}

// useLantern re-renders when the store changes and returns its state.
export function useLantern(): State {
  const session = useSession();
  return useSyncExternalStore(session.store.subscribe, session.store.getSnapshot);
}

// useEveryone is the whole directory, people who have left included, for
// the pages that choose among them: the session knows only those active
// until others are shown. Until it loads, or while not wanted, it is those
// the session knows.
export function useEveryone(wanted = true): Principal[] {
  const session = useSession();
  const [all, setAll] = useState<Principal[] | null>(null);
  useEffect(() => {
    if (!wanted) return;
    let live = true;
    session.api.principals().then(
      (r) => live && setAll(r.principals),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [session, wanted]);
  return all ?? [...session.directory.values()];
}
